import { OrderType } from 'data/Order/types'
import isNil from 'lodash/isNil'

enum Action {
  ADJUST_MARGIN_ORDER = 'AdjustPositionMargin',
  CANCEL = 'CancelOrderRequest',
  CLOSE_MARGIN_ORDER = 'ClosePositionMargin',
  CREATE = 'NewOrderSingle',
  CREATE_MARGIN = 'NewOrderSingleMargin',
  MARGIN_CALL_DETAILS = 'MarginCallDetails',
  POSTIION_MARGIN_DETAILS = 'PositionMarginDetails',
  SUBSCRIBE = 'subscribe',
  UNSUBSCRIBE = 'unsubscribe'
}

enum Channel {
  AUTH = 'auth',
  BALANCES = 'balances',
  HEARTBEAT = 'heartbeat',
  MARKET = 'l2',
  PRICES = 'prices',
  SYMBOLS = 'symbols',
  TICKER = 'ticker-ext',
  TRADES = 'trades',
  TRADING = 'trading'
}

enum Status {
  // We receive 'cancelled' from the websocket and 'canceled' from rest...
  CANCELED = 'canceled',
  CANCELLED = 'cancelled',
  EXPIRED = 'expired',
  FILLED = 'filled',
  // 'new' doesn't exist in the backend, just used to denote that we are waiting
  // for the ACK from the backend saying that the order was successfully created
  NEW = 'new',
  OPEN = 'open',
  PARTIAL = 'partial',
  // caution PART_FILLED does not actually exist on websocket
  // response only on historical rest response
  PART_FILLED = 'part_filled',
  PENDING = 'pending',
  REJECTED = 'rejected'
}

enum Type {
  LIMIT = 'limit',
  MARKET = 'market',
  STOP = 'stop',
  STOP_LIMIT = 'stopLimit'
}

type ValueOf<T> = T[keyof T]

const isCancellable = ({
  ordStatus,
  ordType
}: {
  ordStatus: ValueOf<Status>
  ordType: ValueOf<Type>
}) =>
  ordStatus === Status.OPEN ||
  ordStatus === Status.PARTIAL ||
  (ordStatus === Status.PENDING && (ordType === Type.STOP || ordType === Type.STOP_LIMIT))

const getMarketStatus = (o: OrderType) => {
  return o.ordStatus &&
    (o.ordStatus.toLocaleLowerCase() === Status.CANCELLED ||
      o.ordStatus.toLocaleLowerCase() === Status.CANCELED) &&
    !isNil(o.cumQty) &&
    o.cumQty > 0 &&
    !isNil(o.leavesQty) &&
    !isNil(o.orderQty) &&
    o.leavesQty < o.orderQty
    ? Status.FILLED
    : o.ordStatus
}

const getStopStatus = (o: OrderType) => {
  return !isNil(o.ordStatus) && !isNil(o.stopPx) && o.ordStatus === Status.PENDING
    ? `Trigger @ ${o.stopPx}`
    : o.ordStatus
}

const getStatus = (o: OrderType): string => {
  if (isNil(o.ordType)) return o.ordStatus

  switch (o.ordType) {
    case Type.MARKET:
      return getMarketStatus(o)
    case Type.STOP:
    case Type.STOP_LIMIT:
      return getStopStatus(o)
    default:
      return o.ordStatus
  }
}

enum Tif {
  FOK = 'FOK',
  GTC = 'GTC',
  GTD = 'GTD',
  IOC = 'IOC'
}

const order = {
  getStatus,
  isCancellable,
  status: Status,
  tif: Tif,
  type: Type
}

enum Event {
  POSITION_UPDATED = 'positionUpdated',
  REJECTED = 'rejected',
  SNAPSHOT = 'snapshot',
  SUBSCRIBED = 'subscribed',
  UNSUBSCRIBED = 'unsubscribed',
  UPDATED = 'updated'
}

enum Delay {
  HEARTBEAT = 5e3,
  RETRY = 5e3,
  TIMEOUT = 5e3
}

enum Error {
  AUTH_PENDING = 'Authentication pending'
}

export default {
  MAX_RETRY: 6,
  action: Action,
  channel: Channel,
  delay: Delay,
  error: Error,
  event: Event, // which is 5, 6 - 1, 1 is the initial attempt
  order
}
